const VISITOR_KEY = "pp_event_visitor";
const TOUCH_KEY = "pp_event_touch";
const DISCOVERY_KEY = "pp_event_from_discovery";
const VIEW_WINDOW_MS = 1500;
const viewGuards = new Map();

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function shouldCountView(previousMs, nowMs, windowMs = VIEW_WINDOW_MS) {
  if (!previousMs) return true;
  return nowMs - previousMs >= windowMs;
}

const randomId = () => {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

const readStorage = (store, key) => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (store, key, value) => {
  try {
    store.setItem(key, value);
  } catch {
    /* private mode */
  }
};

export function getVisitorId() {
  if (typeof localStorage === "undefined") return null;
  const existing = readStorage(localStorage, VISITOR_KEY);
  if (UUID_RE.test(String(existing || ""))) return existing;
  const id = randomId();
  writeStorage(localStorage, VISITOR_KEY, id);
  return id;
}

export function markOpenedFromDiscovery() {
  if (typeof sessionStorage === "undefined") return;
  writeStorage(sessionStorage, DISCOVERY_KEY, "1");
}

const externalReferrer = () => {
  if (typeof document === "undefined") return "";
  const referrer = document.referrer || "";
  if (!referrer) return "";
  try {
    const host = new URL(referrer).hostname.toLowerCase();
    if (
      host === "localhost" ||
      host.endsWith(".localhost") ||
      host === "usependpay.com" ||
      host.endsWith(".usependpay.com") ||
      host === "pendpay.ng" ||
      host.endsWith(".pendpay.ng")
    ) {
      return "";
    }
    return referrer.slice(0, 300);
  } catch {
    return "";
  }
};

export function rememberLandingTouch() {
  if (typeof sessionStorage === "undefined" || typeof window === "undefined") return;
  if (readStorage(sessionStorage, TOUCH_KEY)) return;
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");
  const utmSource = params.get("utm_source");
  const referrer = externalReferrer();
  if (!ref && !utmSource && !referrer) return;
  writeStorage(
    sessionStorage,
    TOUCH_KEY,
    JSON.stringify({
      ref: ref || undefined,
      utmSource: utmSource || undefined,
      utmMedium: params.get("utm_medium") || undefined,
      referrer: referrer || undefined,
    })
  );
}

export function currentAttribution(client) {
  rememberLandingTouch();
  let stored = {};
  if (typeof sessionStorage !== "undefined") {
    try {
      stored = JSON.parse(readStorage(sessionStorage, TOUCH_KEY) || "{}") || {};
    } catch {
      stored = {};
    }
  }
  let fromDiscovery = false;
  if (typeof sessionStorage !== "undefined" && readStorage(sessionStorage, DISCOVERY_KEY) === "1") {
    fromDiscovery = true;
    try {
      sessionStorage.removeItem(DISCOVERY_KEY);
    } catch {
      /* ignore */
    }
  }
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  if (fromDiscovery && !stored.ref && !stored.utmSource && !stored.referrer && !params.get("ref") && !params.get("utm_source")) {
    stored = { ref: "pendpay" };
    if (typeof sessionStorage !== "undefined") {
      writeStorage(sessionStorage, TOUCH_KEY, JSON.stringify(stored));
    }
  }
  const ref =
    stored.ref ||
    params.get("ref") ||
    (!stored.utmSource && !stored.referrer && fromDiscovery ? "pendpay" : undefined);
  const referrer = stored.referrer || externalReferrer() || "";
  return {
    visitorId: getVisitorId() || undefined,
    ref: ref || undefined,
    utmSource: stored.utmSource || params.get("utm_source") || undefined,
    utmMedium: stored.utmMedium || params.get("utm_medium") || undefined,
    referrer: referrer ? referrer.slice(0, 300) : undefined,
    analyticsClient: client,
    client,
  };
}

export function beginPageView(eventId, now = Date.now()) {
  const previous = viewGuards.get(eventId) || 0;
  if (!shouldCountView(previous, now)) return null;
  viewGuards.set(eventId, now);
  return randomId();
}

export function interestDedupeKey(eventId) {
  if (typeof sessionStorage === "undefined") return { key: randomId(), fresh: true };
  const storageKey = `pp_interest_key_${eventId}`;
  const existing = readStorage(sessionStorage, storageKey);
  if (existing) return { key: existing, fresh: false };
  const key = randomId();
  writeStorage(sessionStorage, storageKey, key);
  return { key, fresh: true };
}
